import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Save } from "lucide-react";
import { customerService } from "@/lib/database";
import type { Customer } from "@/lib/supabase";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function Customer() {
  const { customerId } = useParams();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    loadCustomer();
  }, []);

  const loadCustomer = async () => {
    try {
      setLoading(true);
      const customer = await customerService.getById(customerId!);

      setCustomer(customer);
    } catch (error) {
      console.error("Error loading customer:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading customer...</div>
      </div>
    );
  }

  const handleSave = async () => {
    if (!customer) return;

    try {
      setSaving(true);
      // const updatedCustomer = await customerService.update(customer.id, customer);
      await customerService.update(customer.id, customer);
    } catch (error) {
      console.error("Error updating customer:", error);
      alert("Error updating customer. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof Customer, value: string) => {
    if (!customer) return;

    setCustomer((prev) => (prev ? { ...prev, [field]: value } : null));
  };

  if (!customer) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              {customer?.first_name} {customer?.last_name}
            </h1>
            <p className="text-gray-600">Update customer information</p>
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#254164] hover:bg-[#1a2d47]"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Saving..." : "Save"}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
            <CardDescription>
              Basic customer details and contact information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>

                <Input
                  id="first_name"
                  value={customer.first_name}
                  onChange={(e) =>
                    handleInputChange("first_name", e.target.value)
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>

                <Input
                  id="last_name"
                  value={customer.last_name}
                  onChange={(e) =>
                    handleInputChange("last_name", e.target.value)
                  }
                />
              </div>

              {/* <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>

                  <Input
                    id="email"
                    type="email"
                    value={customer.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                  />
                </div> */}

              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>

                <Input
                  id="phone"
                  value={customer.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Addresses */}
        <Card>
          <CardHeader>
            <CardTitle>Addresses</CardTitle>
            <CardDescription>Customer shipping addresses</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="us_address">US Address</Label>

              <Textarea
                id="us_address"
                value={customer.us_address}
                onChange={(e) =>
                  handleInputChange("us_address", e.target.value)
                }
                placeholder="US address"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="haiti_address">Haiti Address</Label>

              <Textarea
                id="haiti_address"
                value={customer.haiti_address}
                onChange={(e) =>
                  handleInputChange("haiti_address", e.target.value)
                }
                placeholder="Haiti address"
              />
            </div>
          </CardContent>
        </Card>

        {/* Preferences */}
        {/* <Card>
            <CardHeader>
              <CardTitle>Preferences</CardTitle>
              <CardDescription>Customer preferences and account settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2 space-x-2">
                  <Label htmlFor="preferred_language">Preferred Language</Label>

                  <Select
                    value={customer.preferred_language}
                    onValueChange={(value: 'english' | 'french' | 'creole') => handleInputChange('preferred_language', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="english">English</SelectItem>
                      <SelectItem value="french">French</SelectItem>
                      <SelectItem value="creole">Haitian Creole</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 space-x-2">
                  <Label htmlFor="notification_method">Notification Method</Label>

                  <Select
                    value={customer.notification_method}
                    onValueChange={(value: 'email' | 'sms' | 'both') => handleInputChange('notification_method', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 space-x-2">
                  <Label htmlFor="preferred_shipping_method">Preferred Shipping</Label>

                  <Select
                    value={customer.preferred_shipping_method}
                    onValueChange={(value: 'air' | 'cargo') => handleInputChange('preferred_shipping_method', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="air">Air Shipping</SelectItem>
                      <SelectItem value="cargo">Sea</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 space-x-2">
                  <Label>Account Status</Label>
                  <Badge className={customer.account_status === 'active' ? 'bg-green-100 hover:bg-green-100 text-green-800' : 'bg-red-100 hover:bg-red-100 text-red-800'}>
                    {customer.account_status}
                  </Badge>
                </div>

                <div className="space-y-2 space-x-2">
                  <Label>Customer Tier</Label>
                  <Badge className={customer.customer_tier === 'premium' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}>
                    {customer.customer_tier}
                  </Badge>
                </div>
              </div>

              {customer.special_instructions && (
                <div className="space-y-2 space-x-2">
                  <Label htmlFor="special_instructions">Special Instructions</Label>

                  <Textarea
                    id="special_instructions"
                    value={customer.special_instructions || ''}
                    onChange={(e) => handleInputChange('special_instructions', e.target.value)}
                    placeholder="Any special delivery instructions or notes"
                  />
                </div>
              )}
            </CardContent>
          </Card> */}

        {/* Account Information */}
        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
            <CardDescription>
              Account creation and activity details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <Label className="text-gray-500">Created</Label>
                <p>{new Date(customer.created_at!).toLocaleDateString()}</p>
              </div>
              <div>
                <Label className="text-gray-500">Last Updated</Label>
                <p>{new Date(customer.updated_at!).toLocaleDateString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
